/**
 * Der Abschnitt „DSV-Belege" (Zustellbeleg + Rechnung, Auftrag "DSV-Belege (Zustellbeleg + Rechnung)
 * in der Bestellung anzeigen", 04.09.2026) - beide Belegtypen kommen als eigene, NICHT bearbeitbare
 * Zeilen aus je einem eigenen DSV-Webhook (`dsv_delivery_receipts`/`dsv_invoices`, Migrationen
 * 034/035), gelesen ueber `GET /api/orders/:orderId/dsv-belege` (`backend/src/http/routes/
 * dsvBelege.ts`). Bauplan wie `ShipTab/DsvHistorie.tsx`: rein darstellend, bekommt die Liste als
 * Prop, kein eigener Ladezustand DAFUER - die einzige eigene Zustandshaltung hier ist die
 * Fehlermeldung eines gescheiterten DOWNLOADS (siehe unten), kein zweiter "Lade ich die Liste
 * selbst?"-Pfad.
 *
 * EINGEBUNDEN AN ZWEI STELLEN (Auftraggeber-Korrektur, 05.09.2026): im Reiter „Spedition"
 * (`ShipTab/SpeditionTab.tsx`, neben Historie/Stationen) UND im Reiter „Dokumente"
 * (`DocsTab/DokumenteTab.tsx`, als eigener, klar abgesetzter, rein lesender Abschnitt neben der
 * editierbaren Checkliste) - DIESELBE Komponente an beiden Stellen, keine zweite Fassung. Deshalb
 * liegt diese Datei NICHT unter `ShipTab/` (wo sie urspruenglich stand), sondern hier direkt unter
 * `Drawer/` - derselbe neutrale Ort wie `Abschnitt.tsx`/`Datumsfeld.tsx`, die ebenfalls von mehr als
 * einem Reiter benutzt werden (Nachbesserung, Pruefer-Befund "Sollte" 8, 05.09.2026: eine
 * gemeinsam genutzte Komponente gehoert nicht in einen reiterspezifischen Ordner).
 *
 * ================= Download: Blob-Umweg statt eines einfachen `<a href>` =================
 *
 * Das Frontend authentifiziert AUSSCHLIESSLICH per `Authorization: Bearer <token>`
 * (`api/basis.ts#standardKopfzeilen`) - ein `<a href="/api/orders/.../datei" download>` kann diesen
 * Header nicht mitschicken, das Backend liest den Token aber ausschliesslich daraus
 * (`backend/src/http/middleware/authenticate.ts:50`, keine Cookie-/Query-Auswertung). Gemessen VOR
 * dieser Komponente: `GET /api/files/:fileId` (dieselbe Authentifizierung) hat im Frontend HEUTE gar
 * keinen Aufrufer - das einzige bestehende Vorbild fuer einen authentifizierten Binaerabruf ist
 * `components/Artikelbild.tsx#useAuthentifiziertesBild` (`api/basis.ts#holeBinaerMitStatus`, dort fuer
 * die INLINE-ANZEIGE eines Bilds als Object-URL in `<img src>`). Diese Komponente uebernimmt denselben
 * Baustein (`holeBinaerMitStatus`), aber fuer einen ECHTEN DATEI-DOWNLOAD statt einer Anzeige:
 * `api/basis.ts#ladeUndSpeichereDatei` haengt die Object-URL an ein voruebergehendes `<a download>`,
 * klickt es programmatisch und gibt die Object-URL danach wieder frei (siehe dortiger Kopfkommentar).
 *
 * ================= Eine Rechnung ohne bereits heruntergeladenes PDF =================
 *
 * Nachbesserung (Pruefer-Befund "Sollte" 4, 05.09.2026): `beleg.dateiVerfuegbar === false` (nur bei
 * einer Rechnung moeglich, deren PDF-Download noch aussteht - siehe Backend-Kopfkommentar an
 * `DsvBelegZeile.dateiVerfuegbar`) zeigt statt der Groesse den Hinweis "Datei noch nicht verfügbar"
 * und deaktiviert den Download-Knopf - vorher erschien eine solche Zeile als "0 B" mit einem
 * Knopf, der zwangslaeufig an einem 404 gescheitert waere, ohne erkennbaren Grund fuer den Nutzer.
 */
import { useDateiDownload } from "../../api/useDateiDownload.ts";
import { fmtBytes, fmtDate } from "../../lib/format.ts";
import { Ikone } from "../Ikone.tsx";
import { BelegKategorieWahl, type BelegKategorieEinstellung } from "./DocsTab/BelegKategorieWahl.tsx";
import type { DsvBeleg } from "../../lib/types.ts";

export interface DsvBelegeProps {
  bestellungId: string;
  /** `null`/`undefined` heisst: noch nicht geladen. */
  belege?: DsvBeleg[] | null;
  /**
   * Die Auswahl „Kategorie zuweisen …“ je Beleg (Spec 2026-10-17): Typliste (bereits nach Recht
   * gefiltert), Sendungsbezug und Neuladen kennt der umgebende Reiter. Fehlt die Angabe oder ist die
   * Typliste leer, gibt es keine Auswahl; ebenso fuer jede Zeile ohne verfuegbare Datei.
   */
  kategorie?: BelegKategorieEinstellung;
}

const DOWNLOAD_FEHLGESCHLAGEN_MELDUNG = "Der Beleg konnte nicht heruntergeladen werden.";

/** API-Label plus das zum Belegtyp passende Symbol aus der bestehenden Ikone-Registratur. */
function typLabelUndSymbol(beleg: DsvBeleg): { label: string; symbol: string } {
  if (beleg.typ === "rechnung") return { label: beleg.typAnzeige, symbol: "receipt-text" };
  if (beleg.typ === "dsv-dokument") {
    return { label: beleg.typAnzeige, symbol: "file-text" };
  }
  return { label: beleg.typAnzeige, symbol: "package-check" };
}

/**
 * Ergebnis der automatischen Kostenverteilung einer Rechnung (Plan 2026-09-14, Task 5): "Kosten
 * verteilt" bzw. "Kosten nicht verteilt: <Grund>". Eine nie bewertete Rechnung (Altbestand) und alle
 * anderen Belegtypen tragen keinen Hinweis.
 */
function verteilungsHinweis(beleg: DsvBeleg): string | null {
  const verteilung = beleg.verteilung;
  if (beleg.typ !== "rechnung" || !verteilung) return null;
  if (verteilung.status === "verteilt") return "Kosten verteilt";
  if (verteilung.status === "nicht_verteilt") {
    return verteilung.grundText ? `Kosten nicht verteilt: ${verteilung.grundText}` : "Kosten nicht verteilt";
  }
  return null;
}

/** Ob der Beleg als Dokument bei der Bestellung steht, sonst der Grund (Backend: `hubAblage`). Zustellbelege tragen keinen Hinweis. */
function hubHinweis(beleg: DsvBeleg): string | null {
  const ablage = beleg.hubAblage;
  if (!ablage) return null;
  if (ablage.abgelegt) return "Als Dokument abgelegt";
  return ablage.grund ? `Nicht als Dokument abgelegt: ${ablage.grund}` : "Nicht als Dokument abgelegt";
}

export function DsvBelege({ bestellungId, belege, kategorie }: DsvBelegeProps) {
  const { fehler, herunterladen: herunterladenDatei } = useDateiDownload(DOWNLOAD_FEHLGESCHLAGEN_MELDUNG);

  if (!belege || belege.length === 0) {
    return <p className="calc-note">Keine DSV-Belege vorhanden.</p>;
  }

  async function herunterladen(beleg: DsvBeleg): Promise<void> {
    const pfad = `/api/orders/${encodeURIComponent(bestellungId)}/dsv-belege/${encodeURIComponent(beleg.id)}/datei`;
    await herunterladenDatei(pfad, beleg.dateiname);
  }

  return (
    <>
      <ul className="dsv-belege-liste">
        {belege.map((beleg) => {
          const { label, symbol } = typLabelUndSymbol(beleg);
          return (
            <li key={beleg.id} className="dsv-belege-zeile">
              <span className="dsv-belege-typ">
                <Ikone name={symbol} />
                {label}
              </span>
              <span className="dsv-belege-dateiname">{beleg.dateiname}</span>
              <span className="dsv-belege-groesse">
                {beleg.dateiVerfuegbar ? fmtBytes(beleg.groesseBytes) : "Datei noch nicht verfügbar"}
              </span>
              <span className="dsv-belege-datum">{beleg.datum ? fmtDate(beleg.datum) : "—"}</span>
              {beleg.typ === "rechnung" ? (
                <span className="dsv-belege-rechnungsdaten">
                  {beleg.rechnungsnummer ? `Nr. ${beleg.rechnungsnummer}` : null}
                  {beleg.faelligAm ? ` · fällig ${fmtDate(beleg.faelligAm)}` : null}
                </span>
              ) : null}
              {verteilungsHinweis(beleg) ? (
                <span className="dsv-belege-verteilung">{verteilungsHinweis(beleg)}</span>
              ) : null}
              {beleg.manuelleZuordnung ? (
                // Statt des Ablagehinweises: ein manuell zugeordneter Beleg steht als Dokument in der
                // gewaehlten Kategorie, der Hinweis zur automatischen Ablage waere daneben irrefuehrend.
                <span className="dsv-belege-verteilung dsv-belege-zuordnung">
                  {`Zugeordnet: ${beleg.manuelleZuordnung.label} (manuell)`}
                </span>
              ) : hubHinweis(beleg) ? (
                <span className="dsv-belege-verteilung dsv-belege-hub">{hubHinweis(beleg)}</span>
              ) : null}
              {kategorie && beleg.dateiVerfuegbar ? (
                <BelegKategorieWahl
                  bestellungId={bestellungId}
                  belegId={beleg.id}
                  dateiname={beleg.dateiname}
                  aktuellerTyp={beleg.aktuellerHubTyp}
                  {...kategorie}
                />
              ) : null}
              <button
                type="button"
                className="chk-btn dsv-belege-download"
                disabled={!beleg.dateiVerfuegbar}
                title={beleg.dateiVerfuegbar ? undefined : "Datei noch nicht verfügbar"}
                onClick={() => void herunterladen(beleg)}
              >
                Herunterladen
              </button>
            </li>
          );
        })}
      </ul>
      {fehler ? <div role="alert">{fehler}</div> : null}
    </>
  );
}
